/**
 * problem-sheet.tsx — the sheet a failure leaves, wherever it stops: the
 * notice's sheet laid on the page (NoticeSheet) in coral, an alert, so it is
 * heard as it appears — the round exclamation mark, the title and what the
 * player can count on, the message the failure threw folded under Details,
 * and the ways on. Reload, gold — the one thing left to do — or, where there
 * is a way back (a section's page, to the globe), that way gold and Reload
 * beside it, outlined. The gold action takes the focus as the sheet appears,
 * so Enter takes it. A section's name is its kicker; the version line, its
 * foot on the ground.
 *
 * Where it stands is its caller's: the whole game's on the ground
 * (crash-sheet.tsx), a page's in the main column, and the globe's engine
 * failing to start in the globe's place (game/common/page-problem.tsx).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../ui/button';
import { Disclosure } from '../ui/disclosure';
import { GLYPH } from '../ui/glyphs';
import { NoticeSheet } from '../ui/notice-sheet';
import styles from './problem-sheet.module.css';

/** A way on from the sheet: its words, and what it does. */
interface WayOn {
  label: string;
  onPress: () => void;
}

interface ProblemSheetProps {
  /** What failed, over the title: a section's name. */
  kicker?: string;
  title: string;
  /** What the player can count on, and what to do. */
  sentence: string;
  /** What the failure threw, folded under Details. */
  error: unknown;
  /** Reference for a best-effort anonymous technical report, on the root crash sheet. */
  supportId?: string;
  /** A way back, gold, with Reload outlined beside it: "Back to the globe". Without one, Reload is gold. */
  back?: WayOn;
  /** Under it all: the version line, on the ground. */
  foot?: ReactNode;
}

export function ProblemSheet({ kicker, title, sentence, error, supportId, back, foot }: ProblemSheetProps) {
  const { t } = useTranslation();
  // Reload: the page again, by the same link, which joins again. The game is
  // the server's, so nothing the team has done is lost.
  const reload: WayOn = { label: t('errors.reload'), onPress: () => window.location.reload() };
  const decisive = back ?? reload;
  return (
    <NoticeSheet
      role="alert"
      tone="danger"
      glyph={GLYPH.exclamation}
      kicker={kicker}
      title={title}
      description={sentence}
      fact={supportId ? { label: t('errors.supportId'), value: supportId } : undefined}
      details={
        <Disclosure summary={t('errors.details')}>
          <p className={styles.message}>{thrownText(error) ?? t('errors.detailsUnavailable')}</p>
        </Disclosure>
      }
      actions={
        <>
          {back && (
            <Button variant="outlined" onClick={reload.onPress}>
              {reload.label}
            </Button>
          )}
          <Button tone="primary" autoFocus onClick={decisive.onPress}>
            {decisive.label}
          </Button>
        </>
      }
      foot={foot}
    />
  );
}

/** What a failure threw, as the code threw it — English and technical, for whoever reports it: "TypeError: Cannot read properties of null…". */
function thrownText(error: unknown): string | null {
  try { return error instanceof Error ? `${error.name}: ${error.message}` : String(error); }
  catch { return null; }
}
